import React, { useMemo } from 'react';

import { Flex, Text } from '@adsk/alloy-react-box';
import { AlertWarningIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import {
  isFailedFile,
  getNoPermissionFiles,
  getDeletedFiles,
} from './helpers/formatEntities';
import { useTranslation } from './i18n';

const DocsMissingEntities = ({ loading, error, data }) => {
  const { t } = useTranslation();
  const showCustomMissingEntities =
    !loading && !error && data.map(({ entity }) => entity).some(isFailedFile);

  const {
    tooltipKey,
    messageKey,
    failedCount,
    noPermissionCount,
    deletedCount,
  } = useMemo(() => {
    if (!showCustomMissingEntities) return {};

    const failedFiles = data.map(({ entity }) => entity).filter(isFailedFile);
    const noPermissionFiles = getNoPermissionFiles(failedFiles);
    const deletedFiles = getDeletedFiles(failedFiles);
    const failedCount = failedFiles.length;
    const noPermissionCount = noPermissionFiles.length;
    const deletedCount = deletedFiles.length;

    let tooltipKey;
    let messageKey;

    if (failedCount === 1) {
      // single
      if (noPermissionCount === 1) {
        tooltipKey = 'MISSING_ENTITIES_TOOLTIP_NO_PERMISSION_SINGLE';
        messageKey = 'MISSING_ENTITIES_MESSAGE_SINGLE';
      } else if (deletedCount === 1) {
        tooltipKey = 'MISSING_ENTITIES_TOOLTIP_DELETED_SINGLE';
        messageKey = 'MISSING_ENTITIES_MESSAGE_SINGLE';
      } else {
        tooltipKey = null;
        messageKey = 'MISSING_ENTITIES_MESSAGE_SINGLE_DEFAULT';
      }
    } else {
      // plural
      if (noPermissionCount >= 1 && deletedCount >= 1) {
        // mixed
        tooltipKey = 'MISSING_ENTITIES_TOOLTIP_MIXED';
        messageKey = 'MISSING_ENTITIES_MESSAGE_PLURAL';
      } else if (noPermissionCount === failedCount) {
        // all no permission
        tooltipKey = 'MISSING_ENTITIES_TOOLTIP_NO_PERMISSION_PLURAL';
        messageKey = 'MISSING_ENTITIES_MESSAGE_PLURAL';
      } else if (deletedCount === failedCount) {
        // all deleted
        tooltipKey = 'MISSING_ENTITIES_TOOLTIP_DELETED_PLURAL';
        messageKey = 'MISSING_ENTITIES_MESSAGE_PLURAL';
      } else {
        tooltipKey = null;
        messageKey = 'MISSING_ENTITIES_MESSAGE_PLURAL_DEFAULT';
      }
    }

    return {
      tooltipKey,
      messageKey,
      failedCount,
      noPermissionCount,
      deletedCount,
    };
  }, [showCustomMissingEntities, data]);

  if (!showCustomMissingEntities) return null;

  return (
    <Flex alignItems="center" justifyContent="center" padding="24px">
      {tooltipKey ? (
        <Tooltip content={t(tooltipKey, { deletedCount, noPermissionCount })}>
          <Flex alignItems="center" justifyContent="center">
            <AlertWarningIcon style={{ color: theme.colors.charcoal700 }} />
            <Text typography="bodySmall" color="charcoal700" marginLeft="9px">
              {t(messageKey, { count: failedCount })}
            </Text>
          </Flex>
        </Tooltip>
      ) : (
        <Flex alignItems="center" justifyContent="center">
          <AlertWarningIcon style={{ color: theme.colors.charcoal700 }} />
          <Text typography="bodySmall" color="charcoal700" marginLeft="9px">
            {t('MISSING_ENTITIES_MESSAGE', { count: failedCount })}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

DocsMissingEntities.displayName = 'DocsMissingEntities';

export default DocsMissingEntities;
